/**
 * The one place the app reads `import.meta.env`. Components, hooks and services
 * import `ENV` (or `FEATURES` from ./features) instead of touching Vite's env.
 */

// Nothing per-environment is baked into a built bundle — one image serves dev,
// staging and production. docker-entrypoint.sh writes /runtime-config.js at
// container start (URLs, tokens, Sentry DSN from the deploy stage, plus the
// environment name, DEBUG and FEATURE_* flags from the image's copy of
// .env.<APP_ENV>), and index.html loads it before this bundle. The Vite dev
// server has no entrypoint, so under `npm run dev` everything still comes from
// .env files. No import.meta.env fallback in a build — a missing value must stay
// missing, not silently resolve to whatever the builder happened to have.
const RUNTIME = (typeof window !== 'undefined' && window.__APP_CONFIG__) || {};
export const fromRuntime = (key, devValue) => (import.meta.env.DEV ? devValue : RUNTIME[key]) || '';

// `development` covers the local dev server AND dev.kinematician.com — both read
// .env.development, so they share one set of flags. Locally VITE_APP_ENV wins
// over the Vite mode; in a built image APP_ENV comes from the deploy stage.
const NAME = fromRuntime('APP_ENV', import.meta.env.VITE_APP_ENV || import.meta.env.MODE);

/** .env values are always strings, and the string 'false' is truthy. */
export const readFlag = (value, fallback = false) =>
  value === undefined || value === '' ? fallback : String(value).toLowerCase() === 'true';

export const ENV = Object.freeze({
  name: NAME,
  // The raw Vite mode. Always `production` in a built image — use `name`.
  mode: import.meta.env.MODE,
  isDevelopment: NAME === 'development',
  isStaging: NAME === 'staging',
  isProduction: NAME === 'production',
  // True only under `npm run dev`. A *built* development artifact is not local,
  // so anything pointing at the unbundled /public tree must gate on this.
  isLocal: import.meta.env.DEV,
  isDebug: readFlag(fromRuntime('DEBUG', import.meta.env.VITE_DEBUG), import.meta.env.DEV),
  apiBaseUrl: fromRuntime('API_BASE_URL', import.meta.env.VITE_API_BASE_URL),
  socketUrl: fromRuntime('WS_BASE_URL', import.meta.env.VITE_WS_BASE_URL),
  siteUrl: fromRuntime('BASE_URL', import.meta.env.VITE_BASE_URL),
  recaptchaSiteKey: fromRuntime('SITE_KEY', import.meta.env.VITE_SITE_KEY),
  // Voice-agent switch, not a key — see FEATURES.VOICE_AGENT.
  openaiKey: fromRuntime('OPENAI_KEY', import.meta.env.VITE_OPENAI_KEY),
  sentryDsn: fromRuntime('SENTRY_DSN', import.meta.env.VITE_SENTRY_DSN),
  sentryEnvironment: fromRuntime('SENTRY_ENVIRONMENT', import.meta.env.VITE_SENTRY_ENVIRONMENT),
  sentryTracesSampleRate: fromRuntime(
    'SENTRY_TRACES_SAMPLE_RATE',
    import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
  ),
  // Build-time on purpose: it names the bundle (the git SHA), not the environment.
  sentryRelease: import.meta.env.VITE_SENTRY_RELEASE || import.meta.env.VITE_APP_VERSION || '',
  // Build identity for diagnostics: the git SHA CI bakes in, and the app version if set.
  buildSha: import.meta.env.VITE_SENTRY_RELEASE || '',
  buildVersion: import.meta.env.VITE_APP_VERSION || '',
});
